import { Injectable } from "@angular/core";
import {
  StripePaymentMethodsResponse,
  StripePaymentMethod,
} from "../../models/payment-gateway-responses.interface";
import {
  QPilotPaymentMethod,
  QPilotPaymentMethodType,
} from "../../models/qpilot/payment-method.interface";
import { normalizeCountryCode } from "../../utils/country.utils";

/**
 * Stripe helper service
 * Maps Stripe API responses to QPilot payment method format
 */
@Injectable({
  providedIn: "root",
})
export class StripeHelper {
  /**
   * Map Stripe payment methods response to QPilot payment methods
   * Matches old QMC stripe.helper.ts mapProfiles method
   * @param response Stripe payment methods response
   * @returns Array of QPilot payment methods
   */
  mapProfiles(response: StripePaymentMethodsResponse): QPilotPaymentMethod[] {
    const paymentMethods: QPilotPaymentMethod[] = [];

    if (!response.data || response.data.length === 0) {
      return paymentMethods;
    }

    for (const stripeMethod of response.data) {
      const paymentMethod = this.mapPaymentMethod(stripeMethod);
      paymentMethods.push(paymentMethod);
    }

    return paymentMethods;
  }

  /**
   * Map a single Stripe payment method to QPilot payment method
   * @param stripeMethod Stripe payment method
   * @returns QPilot payment method
   */
  private mapPaymentMethod(
    stripeMethod: StripePaymentMethod,
  ): QPilotPaymentMethod {
    const card = stripeMethod.card;
    const billTo = stripeMethod.billing_details;
    const address = billTo.address;

    // Extract last 4 digits
    const lastFourDigits = card?.last4 || "";

    // Build description from card brand and last 4
    let description = "";
    if (card?.brand && lastFourDigits) {
      description = `${this.formatCardBrand(card.brand)} ending in ${lastFourDigits}`;
    } else if (lastFourDigits) {
      description = `Card ending in ${lastFourDigits}`;
    } else {
      description = "Payment Method";
    }

    // Format expiration as YYYYMM (no hyphen, matches old QMC)
    let expiration: string | undefined;
    if (card?.exp_month && card?.exp_year) {
      const month = card.exp_month.toString().padStart(2, "0");
      expiration = `${card.exp_year}${month}`;
    }

    // Split billing name into first/last
    let firstName: string | undefined;
    let lastName: string | undefined;
    if (billTo.name) {
      const nameParts = billTo.name.split(" ");
      if (nameParts.length > 1) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(" ");
      } else {
        firstName = billTo.name;
      }
    }

    return {
      Id: 0, // Will be set when saved to QPilot
      CustomerId: 0, // Will be set by caller
      Type: QPilotPaymentMethodType.Stripe,
      GatewayCustomerId: stripeMethod.customer,
      GatewayPaymentId: stripeMethod.id,
      Description: description,
      LastFourDigits: lastFourDigits,
      Expiration: expiration,
      BillingFirstName: firstName,
      BillingLastName: lastName,
      BillingStreet1: address?.line1 || undefined,
      BillingStreet2: address?.line2 || undefined,
      BillingCity: address?.city || undefined,
      BillingState: address?.state || undefined,
      BillingPostcode: address?.postal_code || undefined,
      // Normalize country to 2-char ISO 3166-1 alpha-2 (Stripe usually already sends 2-char codes)
      BillingCountry: normalizeCountryCode(address?.country),
      GatewayPaymentType: stripeMethod.type, // card, sepa_debit, etc.
    };
  }

  /**
   * Format Stripe card brand for display
   * @param brand Stripe card brand (visa, mastercard, etc.)
   * @returns Formatted brand name
   */
  private formatCardBrand(brand: string): string {
    const brandMap: Record<string, string> = {
      visa: "Visa",
      mastercard: "Mastercard",
      amex: "American Express",
      discover: "Discover",
      diners: "Diners Club",
      jcb: "JCB",
      unionpay: "UnionPay",
    };

    return brandMap[brand.toLowerCase()] || brand;
  }
}
